'use client';

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import useSessionStoreAndBusinessUnitKeys from '@/lib/hooks/useSessionStoreAndBusinessUnitKeys';
import { BusinessUnit } from '@shared/types/business-unit';
import { Store } from '@shared/types/store';
import useAccount from '@/lib/hooks/useAccount';
import { mapBusinessUnit } from '@/utils/mappers/map-business-unit';
import { mapStore } from '@/utils/mappers/map-store';
import { ContextShape } from './types';
import CategoriesProvider from '../categories';

const initialState = {} as ContextShape;
export const StoreAndBusinessUnitsContext = createContext(initialState);
type Props = {
  children: React.ReactNode;
  businessUnits?: BusinessUnit[];
};
const StoreAndBusinessUnitsProvider = ({ children, businessUnits = [] }: Props) => {
  const memoizedBU = useMemo(() => businessUnits, [businessUnits]);
  const defaultBusinessUnit = memoizedBU[0];
  const { businessUnitKey: sessionBusinessUnitKey } = useAccount();
  const { setBusinessUnitAndStoreSessionKeys } = useSessionStoreAndBusinessUnitKeys();

  const initialBU = useMemo(() => {
    if (sessionBusinessUnitKey) {
      const found = businessUnits.find((bu) => bu.key === sessionBusinessUnitKey);
      if (found) return mapBusinessUnit(found);
    }
    return defaultBusinessUnit ? mapBusinessUnit(defaultBusinessUnit) : undefined;
  }, [businessUnits, sessionBusinessUnitKey, defaultBusinessUnit]);

  const initialStore = useMemo(() => {
    if (initialBU) {
      const store = initialBU.stores?.find((s) => s.key === initialBU.key);
      if (store) return mapStore(store);
    }
    return undefined;
  }, [initialBU]);

  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<BusinessUnit | undefined>(initialBU);
  const [selectedStore, setSelectedStore] = useState<Store | undefined>(initialStore);

  useEffect(() => {
    const currentBU = selectedBusinessUnit?.key;
    const currentStore = selectedStore?.key;

    if (currentBU && currentStore && currentBU === currentStore) {
      void setBusinessUnitAndStoreSessionKeys(currentBU, currentStore);
    }
  }, [selectedBusinessUnit?.key, selectedStore?.key, setBusinessUnitAndStoreSessionKeys]);
  const value = useMemo(
    () => ({
      selectedBusinessUnit,
      setSelectedBusinessUnit,
      selectedStore,
      setSelectedStore,
      businessUnits: memoizedBU,
      defaultBusinessUnit,
    }),
    [selectedBusinessUnit, selectedStore, memoizedBU, defaultBusinessUnit],
  );

  return (
    <StoreAndBusinessUnitsContext.Provider value={value}>
      <CategoriesProvider businessUnitKey={selectedBusinessUnit?.key} storeKey={selectedStore?.key}>
        {children}
      </CategoriesProvider>
    </StoreAndBusinessUnitsContext.Provider>
  );
};

export default StoreAndBusinessUnitsProvider;

export const useStoreAndBusinessUnits = () => {
  return useContext(StoreAndBusinessUnitsContext);
};
